import { useMemo } from 'react';
import clx from 'classnames';
import { useRouter } from 'next/router';
import { useFm, useParams } from '@web3/base-hooks';
import { formatNumber, appPath } from '@web3/base-utils';
import { ArrowchevronRightIcon } from '@bfe/moly-icons';
import { CollaboratorDataItem } from '@/types/home';
import { TabKey } from '../types';
import { StatusTag } from '@/components/status-tag';
import { Avatar } from '@/components/avatar';
import BigNumber from 'bignumber.js';
import { BridgeStatusTag } from '../bridge-status-tag';

import styles from './index.module.scss';

export const Cell = ({
  label,
  content,
  className = '',
  labelClassName = '',
  contentClassName = '',
}) => {
  return (
    <div className={clx(styles.cell, className)}>
      <div className={clx(styles.cellLabel, labelClassName)}>{label}</div>
      <div className={clx(styles.cellContent, contentClassName)}>{content}</div>
    </div>
  );
};

export const ViewMoreDetail = ({ name = '' }) => {
  const fm = useFm();
  const router = useRouter();
  const { locale } = useParams();

  if (!name) return null;

  return (
    <div
      className={styles.viewMore}
      onClick={() => {
        router.push(`/${locale}${appPath}/detail/?entity=${name}`);
      }}
    >
      <span className={styles.vieMoreLabel}>{fm('viewMore')}</span>
      <ArrowchevronRightIcon className={styles.arrow} />
    </div>
  );
};

const CollaboratorCard = (props: {
  data: CollaboratorDataItem & { index: number };
  pageNo: number;
  pageSize: number;
}) => {
  const fm = useFm();
  const { data, pageNo, pageSize } = props;

  const rankNum = useMemo(() => {
    return (pageNo - 1) * pageSize + data?.index + 1 || 1;
  }, [data?.index, pageNo, pageSize]);

  return (
    <div className={styles.container}>
      <div className={styles.tips}>
        <Cell
          label={fm('rank')}
          content={
            <>
              <span>🤝</span>
              <span>{rankNum}</span>
            </>
          }
          contentClassName={styles.flexContent}
        />
        <Cell
          className={styles.yEnd}
          label={fm('endPoint')}
          content={
            <>
              <Avatar
                src={data?.entityLogoUrl}
                size="22"
                name={data?.entityName}
              />
              <span>{data?.entityName}</span>
            </>
          }
          contentClassName={styles.flexContent}
        />
      </div>

      <div className={styles.tips}>
        <Cell
          label={fm('frozenFunds')}
          content={`$${formatNumber(data?.frozenFunds, {
            kmb: false,
            decimal: 0,
            noTrailZero: true,
            roundMode: 1,
          })}`}
        />
        <Cell
          className={styles.yEnd}
          label={fm('casesAssisted')}
          content={data?.caseCount || '--'}
        />
      </div>
    </div>
  );
};

export type EntityCardProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  currentTab: TabKey;
  pageNo: number;
  pageSize: number;
};

/**
 * H5 端卡片形式
 * TODO 经过多次改版，目前样式逻辑混乱，需要重构，将样式和数据分离
 */
export const EntityCard = (props: EntityCardProps) => {
  const { data, currentTab, pageNo, pageSize } = props;
  const fm = useFm();

  const rankNum = useMemo(() => {
    // rank 和 bridge 一样处理
    if (currentTab === TabKey.BRIDGE || currentTab === TabKey.MIXER) {
      return (pageNo - 1) * pageSize + data?.index + 1 || 1;
    }

    return data?.index + 1;
  }, [currentTab, data?.index, pageNo, pageSize]);

  const r2r = () => {
    let label = fm('blockedFunds');
    let amount = data?.blocked;

    switch (currentTab) {
      case TabKey.MIXER:
        label = fm('totalInflow');
        amount = data?.totalInflowFunds;
        break;
      case TabKey.BRIDGE:
        label = fm('untraceableFunds');
        amount = data?.untraceableFunds;
        break;
      case TabKey.AWAIT_RESPONSE:
        label = fm('fundAwaitResponse');
        amount = data?.unblocked;
        break;
    }

    return {
      label,
      amount: `$${formatNumber(amount, {
        kmb: false,
        decimal: 0,
        noTrailZero: true,
        roundMode: 1,
      })}`,
    };
  };

  const r3l = () => {
    let label = '';
    let content = '';

    switch (currentTab) {
      case TabKey.MIXER:
        label = fm('totalTransact');
        // @ts-ignore
        content = data?.totalTransactionCounts;
        break;
      case TabKey.BRIDGE:
        label = fm('fromTo');
        content = `${data?.from} -> ${data?.to}`;
        break;
      default:
        label = fm('lostFunds');
        content = `$${formatNumber(data?.lostFund, {
          kmb: false,
          decimal: 0,
          noTrailZero: true,
          roundMode: 1,
        })}`;
        break;
    }

    return {
      label,
      content,
    };
  };

  const r3r = () => {
    let label = '';
    let content = '';

    switch (currentTab) {
      case TabKey.MIXER:
        label = fm('chain');
        // @ts-ignore
        content = data?.chain;
        break;
      case TabKey.FULLY_SUPPORTED:
        return <ViewMoreDetail name={data?.name} />;
      case TabKey.BRIDGE:
        label = fm('traceableFunds');
        content = `$${formatNumber(data?.traceableFunds, {
          kmb: false,
          decimal: 0,
          noTrailZero: true,
          roundMode: 1,
        })}`;
        break;
      case TabKey.AWAIT_RESPONSE:
        label = fm('nonResponseTime');
        content = data?.awaitingResponseTime || '--';
        break;
    }

    return <Cell className={styles.yEnd} label={label} content={content} />;
  };

  const handleAvatar = () => {
    let logoUrl = '';
    let name = '';

    switch (currentTab) {
      case TabKey.BRIDGE:
      case TabKey.MIXER:
        logoUrl = data?.entityLogoUrl;
        name = data?.entityName;
        break;
      default:
        logoUrl = data?.logo;
        name = data?.name;
        break;
    }

    return (
      <>
        <Avatar src={logoUrl} size="22" name={name} />
        <span>{name}</span>
      </>
    );
  };

  const handleRankIcon = () => {
    switch (currentTab) {
      case TabKey.FULLY_SUPPORTED:
        return '🏆';
      case TabKey.AWAIT_RESPONSE:
        return '☠️';
      case TabKey.MIXER:
        return '🔄';
      default:
        return '🔀';
    }
  };

  const handleStatus = () => {
    switch (currentTab) {
      case TabKey.MIXER:
      case TabKey.BRIDGE:
        return <BridgeStatusTag status={data.status} />;
      default:
        return <StatusTag status={data?.supportStatus} />;
    }
  };

  if (currentTab === TabKey.COLLABORATOR) {
    return <CollaboratorCard data={data} pageNo={pageNo} pageSize={pageSize} />;
  }

  return (
    <div className={styles.container}>
      {/* 所有 tab 第一行共用，都是 rank 和 tag */}
      <div className={styles.tips}>
        <Cell
          label={fm('rank')}
          content={
            <>
              <span>{handleRankIcon()}</span>
              <span>{rankNum}</span>
            </>
          }
          contentClassName={styles.flexContent}
        />
        <div>{handleStatus()}</div>
      </div>
      <div className={styles.tips}>
        <Cell
          label={
            currentTab === TabKey.BRIDGE
              ? fm('fromTo')
              : currentTab === TabKey.MIXER
                ? fm('name')
                : fm('endPoint')
          }
          content={handleAvatar()}
          contentClassName={styles.flexContent}
        />
        <Cell
          className={styles.yEnd}
          label={r2r()?.label}
          content={r2r().amount}
        />
      </div>

      <div
        className={clx(styles.tips, {
          [styles.alignEnd]: currentTab === TabKey.FULLY_SUPPORTED,
        })}
      >
        <Cell label={r3l()?.label} content={r3l()?.content} />
        {r3r()}
      </div>
      {currentTab === TabKey.AWAIT_RESPONSE && (
        <div className={clx(styles.tips, styles.alignEnd)}>
          <Cell
            label={fm('reportsPendingAction')}
            content={
              BigNumber(data?.reportedAddressCount)
                .minus(data?.bannedAddressCount)
                .toNumber() || '--'
            }
          />
          <ViewMoreDetail name={data?.name} />
        </div>
      )}
    </div>
  );
};
